import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Municipality, getYearlyTotal } from '@/lib/data';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from '@/components/ui/button';
import { PieChart, BarChart2, LineChart as LineChartIcon, ArrowDownUp, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';

interface HistoricalChartProps {
  municipality: Municipality;
  compareMunicipalities?: Municipality[];
}

type ChartMode = 'total' | 'breakdown';
type ChartType = 'line' | 'bar';

const BREAKDOWN_LABELS: Record<string, string> = {
  water: 'Vann',
  sewage: 'Avløp',
  waste: 'Renovasjon',
  property: 'Septik',
  chimney: 'Feiing',
};

const HistoricalChart: React.FC<HistoricalChartProps> = ({ municipality, compareMunicipalities = [] }) => {
  const { enabledCategories } = useFeeCategories();
  const [chartMode, setChartMode] = useState<ChartMode>('total');
  const [chartType, setChartType] = useState<ChartType>('line');

  const allMunicipalities = [municipality, ...compareMunicipalities];

  // Define breakdownKeys here so it's accessible throughout the component
  const breakdownKeys = enabledCategories
    .filter(id => BREAKDOWN_LABELS[id])
    .map(id => BREAKDOWN_LABELS[id]);

  // Sjekk om noen av dataene er estimerte
  const hasEstimatedData = municipality.historicalData.some(data => data.isEstimated) ||
    compareMunicipalities.some(m => m.historicalData.some(data => data.isEstimated));

  // Generate merged data for total fees chart
  const totalChartData = municipality.historicalData.map(yearData => {
    const dataPoint: any = {
      year: yearData.year,
      // Legg til info om estimert data i datapunktet
      [`${municipality.name}_estimated`]: yearData.isEstimated ? true : false
    };

    // Add data for primary municipality
    dataPoint[`${municipality.name}`] = getYearlyTotal(yearData, enabledCategories);

    // Add data for comparison municipalities
    compareMunicipalities.forEach(compareCity => {
      const compareYearData = compareCity.historicalData.find(h => h.year === yearData.year);
      if (compareYearData) {
        dataPoint[`${compareCity.name}`] = getYearlyTotal(compareYearData, enabledCategories);
        dataPoint[`${compareCity.name}_estimated`] = compareYearData.isEstimated ? true : false;
      }
    });

    return dataPoint;
  });

  // Generate data for fee breakdown chart (for primary municipality only)
  const breakdownChartData = municipality.historicalData.map(yearData => ({
    year: yearData.year,
    ...(enabledCategories.includes('water') && { 'Vann': yearData.waterFee }),
    ...(enabledCategories.includes('sewage') && { 'Avløp': yearData.sewageFee }),
    ...(enabledCategories.includes('waste') && { 'Renovasjon': yearData.wasteFee }),
    ...(enabledCategories.includes('property') && { 'Septik': yearData.propertyTax }),
    ...(enabledCategories.includes('chimney') && { 'Feiing': yearData.chimneySweepFee }),
    'isEstimated': yearData.isEstimated
  }));
  
  // Velg riktig datasett basert på visningsmodus
  const chartData = chartMode === 'total' ? totalChartData : breakdownChartData;
  
  // Generate a color for each municipality or fee type
  const getColor = (index: number) => {
    const colors = ['#4375C0', '#5B9BD5', '#F4A261', '#E76F51', '#8338EC', '#3A86FF'];
    return colors[index % colors.length];
  };
  
  // Custom tooltip formatter som markerer estimerte data
  const customTooltipFormatter = (value: any, name: string, entry: any, index: any) => {
    // Sjekk om dette datapunktet er estimert
    const isEstimated = chartMode === 'total' 
      ? entry.payload[`${name}_estimated`]
      : entry.payload.isEstimated;
    
    const formattedValue = `${Number(value).toLocaleString('nb-NO')} kr${isEstimated ? ' (estimert)' : ''}`;
    return [formattedValue, name];
  };
  
  // Dynamisk genererte chart-elementer basert på modus
  const generateChartElements = () => {
    if (chartMode === 'total') {
      return allMunicipalities.map((muni, index) => {
        if (chartType === 'line') {
          return (
            <Line
              key={muni.id}
              type="monotone"
              dataKey={muni.name}
              stroke={getColor(index)}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              // Marker estimerte data med attributter som Recharts støtter
              strokeDasharray="0"
              className={`line-${muni.id}`}
            />
          );
        } else {
          return (
            <Bar
              key={muni.id}
              dataKey={muni.name}
              fill={getColor(index)}
              barSize={20}
              // Marker estimerte data med style-klasser for å unngå dynamic props som ikke støttes
              className={`bar-${muni.id}`}
            />
          );
        }
      });
    } else {
      // For nedbrytning, vis bare kommune
      return breakdownKeys.map((key, index) => {
        if (chartType === 'line') {
          return (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={getColor(index)}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              // Bruk statisk verdi for å unngå typefeil
              strokeDasharray="0"
              className={`line-${key}`}
            />
          );
        } else {
          return (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              fill={getColor(index)}
              barSize={40}
              // Bruk klasser for styling istedenfor dynamic props
              className={`bar-${key}`}
            />
          );
        }
      });
    }
  };
  
  // Funksjon for å generere CSS-styling for estimerte datapunkter
  React.useEffect(() => {
    // Legg til custom styling for estimerte datapunkter
    const style = document.createElement('style');
    style.id = 'estimated-data-styles';
    
    const css = chartData.map((data, i) => {
      if (chartMode === 'total') {
        // Styling for totalvisning
        return allMunicipalities.map(muni => {
          const isEstimated = data[`${muni.name}_estimated`];
          if (isEstimated) {
            if (chartType === 'line') {
              return `.line-${muni.id} line { stroke-dasharray: 5 5; }`;
            } else {
              return `.bar-${muni.id} rect[y="${i * 40 + 10}"] { fill-opacity: 0.7; stroke: #000; stroke-width: 1; stroke-dasharray: 2 2; }`;
            }
          }
          return '';
        }).join('\n');
      } else {
        // Styling for breakdown-visning
        if (data.isEstimated) {
          if (chartType === 'line') {
            return breakdownKeys.map(key => `.line-${key} line { stroke-dasharray: 5 5; }`).join('\n');
          } else {
            return breakdownKeys.map(key => `.bar-${key} rect { fill-opacity: 0.7; stroke: #000; stroke-width: 1; stroke-dasharray: 2 2; }`).join('\n');
          }
        }
        return '';
      }
    }).join('\n');
    
    style.innerHTML = css;
    document.head.appendChild(style);
    
    return () => {
      // Cleanup ved unmounting
      const existingStyle = document.getElementById('estimated-data-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, [chartData, chartMode, chartType]);
  
  const renderChart = () => {
    if (chartType === 'line') {
      return (
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="year" 
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            tick={{ fontSize: 12 }} 
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            domain={['dataMin - 1000', 'dataMax + 1000']}
          />
          <Tooltip 
            formatter={customTooltipFormatter}
            labelFormatter={(label) => `År: ${label}`}
          />
          <Legend />
          {generateChartElements()}
        </LineChart>
      );
    } else {
      return (
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="year" 
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            tick={{ fontSize: 12 }} 
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip 
            formatter={customTooltipFormatter}
            labelFormatter={(label) => `År: ${label}`}
          />
          <Legend />
          {generateChartElements()}
        </BarChart>
      );
    }
  };
  
  return (
    <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-slate-100">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Historisk utvikling av kommunale avgifter</h3>
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setChartType(chartType === 'line' ? 'bar' : 'line')}
            title={chartType === 'line' ? 'Vis som stolpediagram' : 'Vis som linjediagram'}
          >
            {chartType === 'line' ? <BarChart2 className="h-4 w-4" /> : <LineChartIcon className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setChartMode(chartMode === 'total' ? 'breakdown' : 'total')}
            title={chartMode === 'total' ? 'Vis avgiftsfordeling' : 'Vis totale avgifter'}
          >
            {chartMode === 'total' ? <PieChart className="h-4 w-4" /> : <ArrowDownUp className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      
      <div className="h-64 md:h-80">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      
      <div className="flex flex-col gap-1 mt-3">
        {hasEstimatedData && (
          <p className="text-xs text-amber-700 flex items-center gap-2">
            <AlertCircle className="h-3 w-3 shrink-0" />
            Estimerte verdier er markert med stiplet linje/mønster. Disse er beregnet fra nyeste tilgjengelige data der historiske data mangler.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Kilde: Statistisk sentralbyrå (SSB) - Tabell 12842: Kommunale avgifter knyttet til bolig ({Math.min(...chartData.map(d => d.year))}-{Math.max(...chartData.map(d => d.year))})
        </p>
        
        {chartMode === 'breakdown' && (
          <p className="text-xs text-muted-foreground">
            <strong>Merk:</strong> "Septik" viser til årsgebyr for septiktømming, og "Feiing" til årsgebyr for feiing og tilsyn.
          </p>
        )}
      </div>
    </div>
  );
};

export default HistoricalChart;
